import { c as _c } from "react/compiler-runtime";
// biome-ignore lint/style/noRestrictedImports: AppLink owns the unlocalized internal-link escape hatch.
import NextLink from 'next/link';
import React, { type ComponentProps } from 'react';
import type { AppLinkProps } from '@/components/elementary/AppLink/types';
import { buildLinkClassName, determineLinkVariant, getAnchorHref, getLinkExceptions, renderContent, resolveAppLinkFields } from '@/components/elementary/AppLink/utils';
import { Link as IntlLink, type LinkProps } from '@/i18n/navigation';
import { isAppLink } from '@/utils/links';
const AppLinkComponent = React.forwardRef<HTMLAnchorElement, AppLinkProps>(({
  href: hrefProp,
  link,
  label: labelProp,
  className,
  children,
  iconEnd,
  iconStart,
  openExternalInNewTab: openExternalInNewTabProp,
  hrefMode: hrefModeProp,
  forceAnchor = false,
  size = 'none',
  inverted,
  underline = false,
  ...props
}, ref) => {
  const resolvedLink = resolveAppLinkFields({
    href: hrefProp,
    link,
    label: labelProp,
    openExternalInNewTab: openExternalInNewTabProp,
    hrefMode: hrefModeProp,
    props
  });
  if (!resolvedLink) return null;
  const {
    href,
    label,
    hrefMode,
    openExternalInNewTab,
    props: resolvedProps
  } = resolvedLink;
  const variant = determineLinkVariant(href, label || children?.toString(), resolvedProps.variant);
  const linkClassName = buildLinkClassName({
    variant,
    size,
    inverted,
    className,
    underline
  });
  const linkException = getLinkExceptions({
    href,
    ref,
    linkClassName,
    props: resolvedProps
  }, () => renderContent({
    variant,
    iconStart,
    iconEnd,
    label,
    children
  }));
  if (linkException) {
    return linkException;
  }
  const isExternal = typeof href === 'string' && !isAppLink(href);
  if (isExternal || forceAnchor) {
    return <a href={getAnchorHref(href, isExternal)} ref={ref} target={openExternalInNewTab ? '_blank' : ''} rel={openExternalInNewTab ? 'noopener noreferrer' : ''} className={linkClassName} {...resolvedProps}>
					{renderContent({
        variant,
        iconStart,
        iconEnd,
        label,
        children
      })}
				</a>;
  }
  if (hrefMode === 'unlocalized') {
    const {
      locale: _locale,
      ...unlocalizedProps
    } = resolvedProps;
    return <NextLink href={href as ComponentProps<typeof NextLink>['href']} ref={ref} className={linkClassName} {...unlocalizedProps}>
					{renderContent({
        variant,
        iconStart,
        iconEnd,
        label,
        children
      })}
				</NextLink>;
  }
  return <IntlLink href={href as LinkProps['href']} ref={ref} className={linkClassName} {...resolvedProps}>
				{renderContent({
      variant,
      iconStart,
      iconEnd,
      label,
      children
    })}
			</IntlLink>;
});
const AppLinkSSRFallback = React.forwardRef<HTMLAnchorElement, AppLinkProps>((props, ref) => {
  const $ = _c(3);
  let t0;
  if ($[0] !== props || $[1] !== ref) {
    t0 = <AppLinkComponent {...props} ref={ref} />;
    $[0] = props;
    $[1] = ref;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
});
export default AppLinkSSRFallback;